import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { Button, Checkbox, FormGroup, FormControlLabel} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import CodeInput from 'react-verification-code-input';
import classnames from 'classnames';
import BasicModal from '../utils/BasicModal';
import ApiManager from '../../libs/ApiManager';
import Header, { VIEWS } from '../utils/Header';
import ErrorMessageContext from '../context/ErrorMessageContext';
import FormContext from '../context/FormContext';

import '../../styles/AccountDeletionContainer.scss';

const STEPS = {
  REQUEST: 0,
  CONFIRM: 1,
  PREMIUM_MODAL: 2,
  RESULT: 3
}

function requestAuthToken () {
  window.location.href = 'alfred-action://feedback-form#account-deletion';
}

function canRevokingApple (provider, platform, version) {
  return provider === 'apple' && platform === 'ios' && version >= 3149;
}

function RequestPage ({ show, email, request = () => {} }) {
  const [agree, setAgree] = useState(false);

  if (!show) return null;
  return <div className='page request'>
    <div className='body'>
      <div className='title'>{t('account_deletion.request.text.1')}</div>
      <div>{t('account_deletion.request.text.2')}</div>
      <div className='subtitle'>{t('account_deletion.request.text.3')}</div>
      <div>{email}</div>
      <div className='subtitle mb16'>{t('account_deletion.request.things.title')}</div>
      <ol className='things'>
        <li>{t('account_deletion.request.things.1')}</li>
        <li>{t('account_deletion.request.things.2')}</li>
        <li>{t('account_deletion.request.things.3')}</li>
      </ol>
      <FormGroup>
        <FormControlLabel
          sx={{ margin: 0 }}
          control={<Checkbox sx={{ padding: 0, marginRight: '12px' }} checked={agree} onChange={(e, checked) => setAgree(checked)} />}
          label={t('account_deletion.request.check')}
        />
      </FormGroup>
    </div>
    <div className='footer'>
      <Button className='submit' onClick={request} disabled={!agree}>{t('account_deletion.request.submit')}</Button>
    </div>
  </div>
}

function ConfirmPage ({ show, email, request, onChange, onComplete, codeInputRef, hasError, timer }) {
  if (!show) return null;
  return <div className='page confirm'>
    <div className='body'>
      <div className='tips'>{t('account_deletion.confirm.tips', { email })}</div>
      <div className='title h1'>{t('account_deletion.confirm.title')}</div>
      <CodeInput
        type='number'
        autoFocus
        ref={codeInputRef}
        className={classnames('code-input', { 'error': hasError })}
        fieldWidth={32}
        fieldHeight={30}
        onChange={onChange}
        onComplete={onComplete}
      />
      {
        hasError ? <div className='error'>{t('account_deletion.confirm.wrong_code_error')}</div> 
          : <div className='placeholder'>{t('account_deletion.confirm.placeholder')}</div>
      }
    </div>
    <div className='footer'>
      <Button className='resend' onClick={request} disabled={timer > 0}>
        {t('account_deletion.confirm.resend')}{timer > 0 && <span>&nbsp;({ timer })</span>}
      </Button>
    </div>
  </div>
}

function ResultPage ({ show, email, deletedAt, onClose }) {
  if (!show) return null;
  return <div className='page result'>
    <div className='body'>
      <span className='account-deletion-success'></span>
      <div className='mb36'>
        <div className='title mb12'>{t('account_deletion.result.title')}</div>
        <div>{t('account_deletion.result.text.1')}</div>
        <div>{t('account_deletion.result.text.2')}</div>
      </div>
      <div className='subtitle'>{t('account_deletion.result.text.3')}</div>
      <div>{email}</div>
      <div className='subtitle mt24'>{t('account_deletion.result.text.4')}</div>
      <div className=''>{deletedAt}</div>
    </div>
    <div className='footer mt36'>
      <Button className='submit' onClick={onClose}>{t('account_deletion.result.ok')}</Button>
      <Button sx={{ mt: 2 }} color='secondary' fullWidth href='https://alfredcamera.zendesk.com/hc/articles/360040013271?utm_source=FeedbackForm&utm_medium=continue&utm_campaign=DeleteAccount#revoke_access'>{t('account_deletion.result.learn_more')}</Button>
    </div>
  </div>
}

export default function AccountDeletionContainer ({
  email,
  token,
  authCode,
  version,
  platform,
  provider,
  isPremium,
  onClose = () => {},
}) {
  const { onError }  = useContext(ErrorMessageContext);
  const { form, onChange, onDiscard }  = useContext(FormContext);
  const [step, setStep] = useState(0);
  const [timer, setTimer] = useState(0);
  const [isWrongCode, setWorngCode] = useState(false);
  const [deletedAt, setDeletedAt] = useState(null);
  const [codeInputValues, setCodeInputValues] = useState([]);
  const [isComplete, setComplete] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const prevAuthCode = useRef(null);
  const codeInput = useRef();
  const timeout = useRef(null);
  const canRevoked = canRevokingApple(provider, platform, version);

  const onConflict = useCallback(() => {
    return onError(new Error(t('account_deletion.conflict.text')));
  }, [onError])

  const requestCode = useCallback(async () => {
    try {
      await ApiManager.requestAccountDeletion(token);
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      setStep(STEPS.CONFIRM);
      setWorngCode(false);
      setComplete(false);
      const end = Date.now() + 60000;
      function updateTimer () {
        const now = Date.now()
        if (now < end) {
          setTimer(Math.ceil((end - now) / 1000));
          timeout.current = setTimeout(updateTimer, 1000);
        } else {
          setTimer(0);
        }
      }
      updateTimer();
      if (codeInput.current) {
        codeInput.current.__clearvalues__();
      }
      await onChange({
        values: {
          account_deletion: STEPS.CONFIRM,
        }
      });
    } catch (err) {
      if (
        err.response &&
        err.response.data &&
        err.response.data.error_code === 40901
      ) {
        onConflict();
      } else {
        onError(err);
      }
    }
  }, [onChange, onConflict, onError, token]);

  useEffect(() => {
    if (authCode && authCode !== prevAuthCode.current) {
      requestCode()
      prevAuthCode.current = authCode;
    }
  }, [authCode, requestCode]);

  useEffect(() => {
    if (form?.values && typeof form?.values['account_deletion'] === 'number') {
      setStep(form?.values['account_deletion']);
    }
  }, [form]);

  useEffect(() => {
    if (codeInput.current) {
      const refs = codeInput.current.iRefs;
      for (const ref of refs) {
        ref.current.setAttribute('inputmode', 'numeric');
        if (isComplete) {
          ref.current.blur();
        }
      }
    }
  }, [isComplete]);

  function yes () {
    return confirm(codeInputValues);
  }

  async function onInputChange (code) {
    setWorngCode(false);
    setComplete(false);
  }

  async function onComplete (code) {
    setCodeInputValues(code);
    setComplete(true);
    if (isPremium) {
      setStep(STEPS.PREMIUM_MODAL);
    } else {
      confirm(code);
    }
  }

  async function request () {
    if (timer > 0) return;
    if (canRevoked) {
      requestAuthToken();
    } else {
      await requestCode();
    }
  }

  async function confirm (code) {
    try {
      const data = await ApiManager.confirmAccountDeletion(code, token, authCode);
      setDeletedAt(data.deleted_at);
      setStep(STEPS.RESULT);
      await onDiscard();
    } catch (err) {
      setStep(STEPS.CONFIRM);
      if (err.response && err.response.data) {
        if (err.response.data.error_code === 40310) {
          setWorngCode(true);
          setComplete(false);
          if (codeInput.current) {
            codeInput.current.__clearvalues__();
          }
        } else if (err.response.data.error_code === 40320) {
          setModalOpen(true);
        } else if (err.response.data.error_code === 40901) {
          onConflict();
        } else {
          onError(err);
        }
      } else {
        onError(err);
      }
      await onChange({
        values: {
          account_deletion: STEPS.CONFIRM,
        }
      });
    }
  }

  async function onPageClose () {
    setStep(STEPS.REQUEST);
    onClose();
    await onChange({
      values: {
        account_deletion: STEPS.REQUEST,
      }
    });
  }

  async function onModalClose () {
    setModalOpen(false);
    setStep(STEPS.REQUEST);
    await onChange({
      values: {
        account_deletion: STEPS.REQUEST,
      }
    });
  }

  const MODAL_DATA = {
    [STEPS.PREMIUM_MODAL]: {
      title: t('account_deletion.premium.title'),
      desc: t('account_deletion.premium.text'),
      yes: t('account_deletion.premium.continue'),
      no: t('account_deletion.premium.cancel'),
    },
  };

  return <div className='account-deletion'>
    <BasicModal
      open={!!MODAL_DATA[step]}
      onClose={onPageClose}
      title={MODAL_DATA[step]?.title}
      description={MODAL_DATA[step]?.desc}
      cancelText={MODAL_DATA[step]?.no}
      confrimText={MODAL_DATA[step]?.yes}
      onCancel={onPageClose}
      onConfirm={yes}
      disableAutoFocus
    />
    <BasicModal
      open={isModalOpen}
      onClose={onModalClose}
      description={`<div style="text-align: center;">${t('account_deletion.error.auth_code_timeout')}</div>`}
      cancelText={t('dialog.ok')}
      onCancel={onModalClose}
      disableAutoFocus
    />
    <Header title={t('account_deletion.header')} view={VIEWS.ACCOUNT_DELETION} backIcon={<CloseIcon />} onBack={onPageClose} />
    <div className='account-deletion-body'>
      <RequestPage show={step >= STEPS.REQUEST && step < STEPS.CONFIRM} email={email} request={request} />
      <ConfirmPage
        show={step >= STEPS.CONFIRM && step < STEPS.RESULT}
        email={email}
        request={request}
        onChange={onInputChange}
        onComplete={onComplete}
        codeInputRef={codeInput}
        hasError={isWrongCode}
        timer={timer}
      />
      <ResultPage
        show={step >= STEPS.RESULT}
        email={email}
        deletedAt={deletedAt}
        onClose={onPageClose}
      />
    </div>
  </div>
}
